import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { cn } from '@shared/utils/cn';
import {
  validateJobDescriptionFile,
  validateJobDescription,
  validateFormInputs,
  handleJobMatchError,
  retryWithErrorHandling
} from '../utils/jobMatchErrorHandler';

interface JobMatchUploadProps {
  onJobMatchCreated?: (jobMatchId: string) => void;
  onCancel?: () => void;
}

interface FormData {
  jobTitle: string;
  companyName: string;
  jobDescription: string;
}

export const JobMatchUpload: React.FC<JobMatchUploadProps> = ({
  onJobMatchCreated,
  onCancel
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    jobTitle: '',
    companyName: '',
    jobDescription: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const extractTextFromFile = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        resolve(text);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) {
      toast.error('No files detected. Please try again.');
      return;
    }

    if (files.length > 1) {
      toast.error('Please upload only one file at a time.');
      return;
    }

    const file = files[0];

    const validationError = validateJobDescriptionFile(file);
    if (validationError) {
      toast.error(validationError.userMessage);
      return;
    }

    setUploadedFile(file);

    try {
      const text = await extractTextFromFile(file);

      const descriptionError = validateJobDescription(text);
      if (descriptionError) {
        toast.error(descriptionError.userMessage);
        setUploadedFile(null);
        return;
      }

      setFormData(prev => ({
        ...prev,
        jobDescription: text
      }));
      toast.success(`Job description loaded successfully (${text.length} characters)`);
    } catch (error) {
      handleJobMatchError(error);
      setUploadedFile(null);
    }
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    const validationError = validateJobDescriptionFile(file);
    if (validationError) {
      toast.error(validationError.userMessage);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploadedFile(file);

    try {
      const text = await extractTextFromFile(file);

      const descriptionError = validateJobDescription(text);
      if (descriptionError) {
        toast.error(descriptionError.userMessage);
        setUploadedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setFormData(prev => ({
        ...prev,
        jobDescription: text
      }));
      toast.success(`Job description loaded successfully (${text.length} characters)`);
    } catch (error) {
      handleJobMatchError(error);
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const removeFile = () => {
    setUploadedFile(null);
    setFormData(prev => ({
      ...prev,
      jobDescription: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = (): boolean => {
    const inputError = validateFormInputs(formData.jobTitle, formData.companyName);
    if (inputError) {
      toast.error(inputError.userMessage);
      return false;
    }

    const descriptionError = validateJobDescription(formData.jobDescription);
    if (descriptionError) {
      toast.error(descriptionError.userMessage);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('You must be logged in to create a job match. Please sign in and try again.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await retryWithErrorHandling(async () => {
        const { data: jobMatch, error } = await supabase
          .from('job_matches')
          .insert({
            user_id: user.id,
            job_title: formData.jobTitle.trim(),
            company_name: formData.companyName.trim(),
            job_description: formData.jobDescription.trim(),
            status: 'pending'
          })
          .select()
          .maybeSingle();

        if (error) throw error;

        if (!jobMatch) {
          throw new Error('Failed to create job match - no data returned');
        }

        try {
          await supabase
            .from('match_history')
            .insert({
              user_id: user.id,
              job_match_id: jobMatch.id,
              interaction_type: 'created',
              metadata: { source: 'upload_form' }
            });
        } catch (historyError) {
          console.warn('Failed to log match history:', historyError);
        }

        toast.success(`Job match created! Analyzing "${formData.jobTitle}" at ${formData.companyName}...`);

        setFormData({
          jobTitle: '',
          companyName: '',
          jobDescription: ''
        });
        setUploadedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        if (onJobMatchCreated) {
          onJobMatchCreated(jobMatch.id);
        }

        return jobMatch;
      }, 'create job match', 2);
    } catch (error: any) {
      handleJobMatchError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-border/40 p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Add New Job Match
        </h2>
        <p className="text-sm text-gray-600">
          Upload or paste a job description to analyze your compatibility
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <Input
              id="jobTitle"
              name="jobTitle"
              type="text"
              value={formData.jobTitle}
              onChange={handleInputChange}
              placeholder="e.g., Senior Software Engineer"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="companyName"
              name="companyName"
              type="text"
              value={formData.companyName}
              onChange={handleInputChange}
              placeholder="e.g., Tech Corp"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Job Description <span className="text-red-500">*</span>
            </label>

            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 hover:border-gray-400',
                uploadedFile && 'border-green-500 bg-green-50'
              )}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {uploadedFile ? (
                <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    disabled={isSubmitting}
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop a job description file here, or
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSubmitting}
                  >
                    Browse Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,text/plain"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Supported format: .txt (max 5MB)
                  </p>
                </>
              )}
            </div>

            <textarea
              id="jobDescription"
              name="jobDescription"
              value={formData.jobDescription}
              onChange={handleInputChange}
              placeholder="Or paste the full job description here..."
              rows={8}
              className="mt-3 w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 resize-vertical text-sm"
              disabled={isSubmitting}
              required
            />

            {formData.jobDescription && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                {formData.jobDescription.length >= 50 ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                )}
                <span className={cn(
                  'text-xs',
                  formData.jobDescription.length >= 50 ? 'text-green-600' : 'text-yellow-600'
                )}>
                  {formData.jobDescription.length} characters
                  {formData.jobDescription.length < 50 &&
                    ` (minimum 50 required)`
                  }
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Create Job Match
              </>
            )}
          </Button>

          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default JobMatchUpload;
